import { FC, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  keyframes,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { hubGhostBtnSx } from './accountHubStyles';
import {
  contactCopy,
  primaryHint,
  type ContactRowData,
  type ContactType,
} from './contactInfo';

const verifiedChip = (
  <Chip
    label="Verified"
    size="small"
    sx={{
      backgroundColor: 'primary.lighter',
      color: 'primary.dark',
      fontWeight: 500,
      fontSize: 11,
      borderRadius: '12px',
      height: 'auto',
      '& .MuiChip-label': { px: 1, py: 0.25 },
    }}
  />
);

interface ContactRowProps {
  type: ContactType;
  row: ContactRowData;
  index: number;
  /** First name of the contact owner when editing another person's contacts. */
  ownerName?: string;
  glowId: number | null;
  fadeId: number | null;
  editingId: number | null;
  editingValue: string;
  editingError: string | null;
  setPrimaryLoadingId: number | null;
  setPrimaryError: { id: number; detail: string } | null;
  canRemove: boolean;
  onSetPrimary: (id: number) => void;
  onEditStart: (id: number, value: string) => void;
  onEditCancel: () => void;
  onEditValueChange: (value: string) => void;
  onEditSave: (id: number) => void;
  onRemove: (id: number, value: string) => void;
  onGlowEnd: (id: number) => void;
}

const ContactRow: FC<ContactRowProps> = ({
  type,
  row,
  index,
  ownerName,
  glowId,
  fadeId,
  editingId,
  editingValue,
  editingError,
  setPrimaryLoadingId,
  setPrimaryError,
  canRemove,
  onSetPrimary,
  onEditStart,
  onEditCancel,
  onEditValueChange,
  onEditSave,
  onRemove,
  onGlowEnd,
}) => {
  const theme = useTheme();
  const copy = contactCopy[type];

  const blueGlow = useMemo(
    () =>
      keyframes`
        0%, 100% {
          box-shadow: 0 0 0 0 ${alpha(theme.palette.info.main, 0)};
        }
        50% {
          box-shadow: 0 0 20px 4px ${alpha(theme.palette.info.main, 0.6)};
        }
      `,
    [theme.palette.info.main]
  );

  const fadeIn = useMemo(
    () =>
      keyframes`
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      `,
    []
  );

  const isEditing = editingId === row.id;
  const isGlowing = glowId === row.id;
  const isFading = fadeId === row.id;
  const setPrimaryInProgress = setPrimaryLoadingId !== null;

  return (
    <Box
      onAnimationEnd={(event) => {
        if (event.animationName === blueGlow.name) {
          onGlowEnd(row.id);
        }
      }}
      sx={{
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -6,
          right: -6,
          bottom: -6,
          left: -6,
          borderRadius: 1,
          pointerEvents: 'none',
          animation: isGlowing ? `${blueGlow} 3s ease-in-out 2` : 'none',
        },
      }}
    >
      {index > 0 && <Divider sx={{ my: 1.5 }} />}
      <Stack gap={0.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {row.isPrimary ? copy.primaryLabel : copy.otherLabel}
          </Typography>
          <Stack direction="row" gap={0.5}>
            {!row.isPrimary && !isEditing && (
              <Button
                size="small"
                variant="contained"
                disableElevation
                sx={hubGhostBtnSx}
                data-testid={`set-primary-${type}-${row.id}`}
                disabled={setPrimaryInProgress}
                loading={setPrimaryLoadingId === row.id}
                loadingIndicator={
                  <CircularProgress
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.secondary.light
                          : theme.palette.secondary.dark,
                    })}
                    size={14}
                  />
                }
                onClick={() => onSetPrimary(row.id)}
              >
                Set Primary
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={isEditing || setPrimaryInProgress}
              sx={hubGhostBtnSx}
              onClick={() => onEditStart(row.id, row.value)}
            >
              Edit
            </Button>
            {!isEditing &&
              (canRemove ? (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={setPrimaryInProgress}
                  sx={(theme) => ({
                    ...hubGhostBtnSx(theme),
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.lighter',
                    },
                  })}
                  onClick={() => onRemove(row.id, row.value)}
                >
                  Remove
                </Button>
              ) : null)}
          </Stack>
        </Stack>

        {setPrimaryError && setPrimaryError.id === row.id && (
          <Alert
            severity="error"
            sx={{ py: 0 }}
            data-testid={`set-primary-error-${type}-${row.id}`}
          >
            {setPrimaryError.detail}
          </Alert>
        )}

        {row.verified && <Box>{verifiedChip}</Box>}

        {isEditing ? (
          <Stack gap={1}>
            {editingError && (
              <Alert severity="error" sx={{ py: 0 }}>
                {editingError}
              </Alert>
            )}
            <TextField
              value={editingValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              type={type === 'phone' ? 'tel' : 'email'}
              variant="standard"
              autoFocus
              fullWidth
              inputProps={{
                style: { fontSize: 14, fontWeight: 500 },
                ...(type === 'phone' ? { maxLength: 14 } : {}),
              }}
            />
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button size="small" onClick={onEditCancel}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!editingValue.trim()}
                onClick={() => onEditSave(row.id)}
              >
                Save
              </Button>
            </Stack>
            {row.isPrimary && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {primaryHint(type, ownerName)}
              </Typography>
            )}
          </Stack>
        ) : (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              ...(type === 'email' ? { wordBreak: 'break-all' as const } : {}),
              animation:
                isGlowing || isFading ? `${fadeIn} 1s ease-out` : 'none',
            }}
          >
            {row.value}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ContactRow;
